import {process, State} from "@progress/kendo-data-query"
import {Button, Chip} from "@progress/kendo-react-buttons"
import {ExcelExport} from "@progress/kendo-react-excel-export"
import {Field, Form, FormElement} from "@progress/kendo-react-form"
import {Grid, GridColumn, GridDataStateChangeEvent, GridToolbar} from "@progress/kendo-react-grid"
import {Tooltip} from "@progress/kendo-react-tooltip"
import dayjs from "dayjs"
import {MDBBreadcrumb, MDBBreadcrumbItem} from "mdbreact"
import {useEffect, useRef, useState} from "react"
import AddEditTestPackage from "../../components/Fees/addEditTestPackage"
import DealerDropdown from "../../components/Shared/dealerDropdown"
import {hasPermission} from "../../components/Shared/permissionChecker"
import {RootState} from "../../rootReducer"
import {columnProps} from "../../shared/ActiveGridFilter"
import {SpinnerGrid} from "../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../store"
import {processExportData} from "../../utils/common"
import {
    fetchTestPackages,
    getTestPackageDetails,
    setTestPackageDetails,
    showAddEditTestPackageModal,
    TestPackage,
    TestPackageDetails,
} from "./feesSlice"
const initialState: State = {skip: 0, take: 100}

const TestPackages = () => {
    const dispatch = useAppDispatch()
    const _export = useRef<ExcelExport | null>(null)
    const testPackages = useAppSelector((state: RootState) => state.containers.fees.testPackages)
    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const testPackagesLoading = useAppSelector((state: RootState) => state.containers.fees.testPackagesLoading)
    const assignedPermissions = useAppSelector((state: RootState) => state.application.permissions)
    const gridRef = useRef<any>()

    const [notFixedColumnWidth, setNotFixedColumnWidth] = useState<number | undefined>(
        window.innerWidth > 1500 ? undefined : 120,
    )

    const handleResize = () => {
        setNotFixedColumnWidth(window.innerWidth > 1500 ? undefined : 160)
    }

    useEffect(() => {
        if (notFixedColumnWidth === undefined) {
            gridRef.current.headerRef.current.table.style.removeProperty("width")
            gridRef.current.tableElement.style.removeProperty("width")
        }
    }, [notFixedColumnWidth])

    useEffect(() => {
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    useEffect(() => {
        if (selectedDealer) dispatch(fetchTestPackages(selectedDealer?.dealerId))
    }, [dispatch, selectedDealer])

    useEffect(() => {
        setDataState(initialState)
    }, [testPackages])

    const onNewPackageClick = () => {
        dispatch(
            setTestPackageDetails({effectiveDate: dayjs().add(1, "day").startOf("day").toDate()} as TestPackageDetails),
        )
        dispatch(showAddEditTestPackageModal(true))
    }

    const onEditTestPackageLinkClick = (e: any, props: any) => {
        e.preventDefault()
        dispatch(showAddEditTestPackageModal(true))
        dispatch(getTestPackageDetails(selectedDealer?.dealerId ?? 0, parseInt(props)))
    }

    const TestPackageNameCell = (props: any) => (
        <td
            style={{
                textDecoration: "underline",
                cursor: "pointer",
            }}
            colSpan={props.colSpan}
            role={"gridcell"}
            aria-colindex={props.ariaColumnIndex}
            className={!props.dataItem.active ? "text-black-50" : ""}
            title={`${props.dataItem.testPackageName}`}
        >
            <a href=" " onClick={event => onEditTestPackageLinkClick(event, props.dataItem.testPackageID)}>
                {props.dataItem.testPackageName}
            </a>
        </td>
    )

    const deactivatedCell = (props: any) => {
        let cellValue = ""

        if (props.field === "testPackageDescription") cellValue = props.dataItem.testPackageDescription
        else if (props.field === "laneFee") cellValue = props.dataItem.laneFee
        else if (props.field === "testFee") cellValue = props.dataItem.testFee
        return (
            <td
                title={`${props.field === "testPackageDescription" ? props.dataItem.testPackageDescription : ""}`}
                colSpan={props.colSpan}
                role={"gridcell"}
                aria-colindex={props.ariaColumnIndex}
                className={` ${props.field === "laneFee" || props.field === "testFee" ? "text-end pe-5" : ""} ${
                    !props.dataItem.active ? "text-black-50 " : ""
                }`}
            >
                {cellValue}
            </td>
        )
    }

    const TestNamesChipCell = (props: any) => {
        return (
            <td colSpan={props.colSpan} role={"gridcell"} aria-colindex={props.ariaColumnIndex}>
                {(props.dataItem as TestPackage).notes && (
                    <p style={{margin: 0}}>{(props.dataItem as TestPackage).notes}</p>
                )}
                {(props.dataItem as TestPackage).tests.map(t => (
                    <Chip
                        text={t.item2}
                        value={t.item1}
                        disabled={true}
                        selected={false}
                        removable={false}
                        style={{marginRight: "5px"}}
                    />
                ))}
            </td>
        )
    }

    const exportExcelData = (testPackages: any[]) =>
        testPackages.map(({tests, ...item}) => ({
            ...item,
            tests: tests.map((a: any) => a.item2).join(", "),
            effectiveDate: item.effectiveDate != null ? dayjs(item.effectiveDate).format("YYYY-MM-DD") : "",
        }))

    const [dataState, setDataState] = useState(initialState)
    return (
        <>
            <MDBBreadcrumb>
                <MDBBreadcrumbItem> Settings </MDBBreadcrumbItem>
                <MDBBreadcrumbItem active>Test Packages</MDBBreadcrumbItem>
            </MDBBreadcrumb>
            <Form
                render={() => (
                    <div
                        className="mb-2"
                        style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "end",
                        }}
                    >
                        <FormElement style={{width: 300}}>
                            <fieldset className={"k-form-fieldset"}>
                                <Field
                                    onChange={(e: any) => dispatch(fetchTestPackages(parseInt(e.value)))}
                                    name={"feesDealerDropdown"}
                                    component={DealerDropdown}
                                />
                            </fieldset>
                        </FormElement>
                    </div>
                )}
            />
            <div className="position-relative">
                {testPackagesLoading && <SpinnerGrid />}
                <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                    <ExcelExport
                        fileName={crypto.randomUUID()}
                        data={exportExcelData(processExportData(testPackages, dataState))}
                        ref={_export}
                    >
                        <Grid
                            style={{
                                height: "calc(100vh - 13.1rem)",
                            }}
                            data={process(testPackages, dataState)}
                            {...dataState}
                            sortable={true}
                            pageable={true}
                            onDataStateChange={(e: GridDataStateChangeEvent) => setDataState(e.dataState)}
                            ref={gridRef}
                        >
                            {hasPermission(["TestPackages.Add"], assignedPermissions) && (
                                <GridToolbar>
                                    <Button
                                        fillMode="flat"
                                        themeColor={"primary"}
                                        className="buttons-container-button"
                                        iconClass="fa fa-plus"
                                        onClick={onNewPackageClick}
                                        disabled={selectedDealer ? false : true}
                                    >
                                        New Package
                                    </Button>
                                    <Button
                                        fillMode="flat"
                                        themeColor={"primary"}
                                        className="buttons-container-button"
                                        iconClass="fa fa-file-excel"
                                        disabled={process(testPackages, dataState).total === 0}
                                        onClick={() => _export.current !== null && _export.current.save()}
                                    >
                                        Export to Excel
                                    </Button>
                                </GridToolbar>
                            )}
                            <GridColumn
                                {...columnProps("testPackageName", dataState)}
                                field="testPackageName"
                                title="Test Package Name"
                                width={notFixedColumnWidth}
                                cell={props => TestPackageNameCell(props)}
                            />
                            <GridColumn
                                {...columnProps("testPackageDescription", dataState)}
                                field="testPackageDescription"
                                title="Description"
                                width={notFixedColumnWidth}
                                cell={props => deactivatedCell(props)}
                            />
                            <GridColumn
                                {...columnProps("laneFee", dataState)}
                                field="laneFee"
                                title="Lane Fee"
                                width={"200px"}
                                cell={props => deactivatedCell(props)}
                            />
                            <GridColumn
                                {...columnProps("testFee", dataState)}
                                field="testFee"
                                title="Test Fee"
                                width={"200px"}
                                cell={props => deactivatedCell(props)}
                            />
                            <GridColumn
                                {...columnProps("duration", dataState)}
                                field="duration"
                                title="Duration"
                                width={"125px"}
                                className={"text-end pe-5"}
                            />
                            <GridColumn
                                {...columnProps("effectiveDate", dataState)}
                                field="effectiveDate"
                                title="Prices changing on"
                                width={"180px"}
                                filter={"date"}
                                format="{0:d}"
                                className={"text-end pe-5"}
                            />
                            <GridColumn
                                field="tests"
                                title="Tests"
                                cell={props => TestNamesChipCell(props)}
                                width={notFixedColumnWidth}
                            />
                        </Grid>
                    </ExcelExport>
                </Tooltip>
            </div>
            <AddEditTestPackage />
        </>
    )
}

export default TestPackages
